import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from "firebase/firestore";
import { Link } from 'react-router-dom';
import { TrendingUp, TrendingDown, IndianRupee, FileText, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import Spinner from './Spinner.js';

const StatCard = ({ title, value, icon, color, trend }) => (
    <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        flex: 1,
        minWidth: '200px'
    }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
            <div style={{ padding: '10px', borderRadius: '10px', backgroundColor: `${color}15`, color: color }}>
                {icon}
            </div>
            {trend && (
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: trend > 0 ? '#10b981' : '#ef4444' }}>
                    {trend > 0 ? '+' : ''}{trend}%
                </span>
            )}
        </div>
        <h3 style={{ margin: 0, fontSize: '14px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</h3>
        <p style={{ margin: '5px 0 0', fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>
            ₹{value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
        </p>
    </div>
);

const AccountsDashboard = ({ db }) => {
    const [stats, setStats] = useState({
        totalRevenue: 0,
        totalExpenses: 0,
        pendingReceivables: 0,
        cashFlow: 0
    });
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [upcomingInvoices, setUpcomingInvoices] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const transSnap = await getDocs(collection(db, "accounts_transactions"));
                const invSnap = await getDocs(collection(db, "accounts_invoices"));

                let rev = 0;
                let exp = 0;
                let pending = 0;

                const transactions = transSnap.docs.map(doc => {
                    const data = doc.data();
                    if (data.type === 'revenue') rev += data.amount;
                    else exp += data.amount;
                    return { id: doc.id, ...data };
                });

                const invoices = invSnap.docs.map(doc => {
                    const data = doc.data();
                    if (data.status !== 'paid') pending += (data.balance || 0);
                    return { id: doc.id, ...data };
                });

                setStats({
                    totalRevenue: rev,
                    totalExpenses: exp,
                    pendingReceivables: pending,
                    cashFlow: rev - exp
                });

                setRecentTransactions(transactions.sort((a,b) => new Date(b.date) - new Date(a.date)).slice(0, 5));
                setUpcomingInvoices(invoices.filter(i => i.status !== 'paid').sort((a,b) => new Date(a.dueDate) - new Date(b.dueDate)).slice(0, 5));

            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [db]);

    if (loading) return <Spinner />;

    return (
        <div style={{ paddingBottom: '50px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <h2 style={{ margin: 0 }}>Accounts Overview</h2>
                {/* Removed Ledger and Invoices links as they are in the sidebar now */}
            </div>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '40px' }}>
                <StatCard title="Total Revenue" value={stats.totalRevenue} icon={<TrendingUp size={20}/>} color="#10b981" />
                <StatCard title="Total Expenses" value={stats.totalExpenses} icon={<TrendingDown size={20}/>} color="#ef4444" />
                <StatCard title="Pending Receivables" value={stats.pendingReceivables} icon={<Clock size={20}/>} color="#f59e0b" />
                <StatCard title="Net Cash Flow" value={stats.cashFlow} icon={<IndianRupee size={20}/>} color="#2563eb" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '30px' }}>
                {/* Recent Transactions */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px' }}>Recent Transactions</h3>
                        <Link to="/accounts/transactions" style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}>View All</Link>
                    </div>
                    {recentTransactions.map(t => (
                        <div key={t.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                            <div style={{ 
                                width: '8px', height: '8px', borderRadius: '50%', 
                                backgroundColor: t.type === 'revenue' ? '#10b981' : '#ef4444',
                                marginRight: '15px'
                            }} />
                            <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '14px', fontWeight: '600' }}>{t.category}</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>{new Date(t.date).toLocaleDateString()}</div>
                            </div>
                            <div style={{ fontWeight: 'bold', color: t.type === 'revenue' ? '#059669' : '#dc2626' }}>
                                {t.type === 'revenue' ? '+' : '-'}₹{t.amount.toLocaleString()}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Upcoming Dues */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px' }}>Upcoming Receivables</h3>
                        <Link to="/accounts/invoices" style={{ fontSize: '12px', color: '#2563eb', textDecoration: 'none' }}>Manage Invoices</Link>
                    </div>
                    {upcomingInvoices.map(i => {
                        const today = new Date();
                        today.setHours(0,0,0,0);
                        const dueDate = new Date(i.dueDate);
                        dueDate.setHours(0,0,0,0);

                        const isOverdue = i.balance > 0 && dueDate < today;
                        const statusColor = isOverdue ? '#ef4444' : '#64748b';
                        const statusText = isOverdue ? '(OVERDUE)' : '';

                        return (
                            <div key={i.id} style={{ display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '14px', fontWeight: '600' }}>{i.customerName}</div>
                                    <div style={{ fontSize: '12px', color: statusColor }}>
                                        Due: {new Date(i.dueDate).toLocaleDateString()} {statusText}
                                    </div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 'bold' }}>₹{i.balance.toLocaleString()}</div>
                                    <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#94a3b8' }}>Inv: {i.invoiceNo}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default AccountsDashboard;
